/**
 * TREATX-11D-WHATSAPP-ADMIN-UI-01 (2026-06-15)
 * --------------------------------------------
 * WhatsApp Admin UI
 *
 * UI-ONLY sprint. Consumes ONLY the existing, mounted endpoints:
 *
 *   GET  /api/admin/whatsapp/templates
 *   POST /api/admin/whatsapp/templates/upsert      (Idempotency-Key)
 *   GET  /api/admin/whatsapp/opt-outs
 *   POST /api/admin/whatsapp/opt-out
 *   POST /api/whatsapp/send-template               (Idempotency-Key)
 *
 * No new service, no new singleton, no new endpoint, no new collection
 * or projection / cache / bridge. The backend (NotificationService +
 * whatsapp_template_service + whatsapp_outbound_service +
 * whatsapp_observability_bridge) is the canonical source of truth and
 * is REUSED EXACTLY as-is.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Plus, RefreshCw, Send, Shield, ShieldOff,
  Phone, MessageSquare, FileText, CheckCircle2, AlertTriangle, Clock, Info,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '../../components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../../components/ui/tabs';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import api from '../../utils/api';

/** Generate a simple client-side idempotency key. The backend treats this as
 *  opaque and persists it on send. */
function idemKey(prefix) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function fmt(iso) {
  if (!iso) return '—';
  try {
    return formatDateTimeLocale(iso);
  } catch {
    return iso;
  }
}

const statusBadge = (status) => {
  const v = (status || 'draft').toLowerCase();
  if (v === 'approved') {
    return <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200" data-testid={`wa-template-status-${v}`}>approved</Badge>;
  }
  if (v === 'disabled') {
    return <Badge className="bg-slate-200 text-slate-600 border-slate-300" data-testid={`wa-template-status-${v}`}>disabled</Badge>;
  }
  return <Badge className="bg-amber-100 text-amber-700 border-amber-200" data-testid={`wa-template-status-${v}`}>{v}</Badge>;
};

const categoryBadge = (cat) => (
  <Badge variant="outline" className="text-[10px]" data-testid={`wa-template-category-${(cat||'').toLowerCase()}`}>
    {cat || 'utility'}
  </Badge>
);

// ─────────────────────────────────────────────────────────────────────
// Template Upsert Modal
// ─────────────────────────────────────────────────────────────────────

const EMPTY_FORM = {
  template_name: '',
  language: 'en_US',
  category: 'utility',
  status: 'draft',
  components_text: '[]',
};

const UpsertModal = ({ open, onOpenChange, initial, onSaved }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          template_name: initial.template_name || '',
          language: initial.language || 'en_US',
          category: initial.category || 'utility',
          status: initial.status || 'draft',
          components_text: JSON.stringify(initial.components || [], null, 2),
        });
      } else {
        setForm(EMPTY_FORM);
      }
      setError(null);
    }
  }, [open, initial]);

  const submit = async () => {
    setError(null);
    let parsedComponents = [];
    try {
      parsedComponents = JSON.parse(form.components_text || '[]');
      if (!Array.isArray(parsedComponents)) throw new Error('components must be a JSON array');
    } catch (e) {
      setError(`Invalid components JSON: ${e.message}`);
      return;
    }
    if (!form.template_name.trim()) {
      setError('Template name is required');
      return;
    }
    setSaving(true);
    try {
      const r = await api.post(
        '/admin/whatsapp/templates/upsert',
        {
          template_name: form.template_name.trim(),
          language: form.language || 'en_US',
          category: form.category || 'utility',
          components: parsedComponents,
          status: form.status || 'draft',
        },
        { headers: { 'Idempotency-Key': idemKey('wa-upsert') } },
      );
      toast({ title: 'Template saved', description: `${form.template_name} (${form.status})` });
      onOpenChange(false);
      if (onSaved) onSaved(r.data && r.data.template);
    } catch (e) {
      const msg = (e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message;
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="wa-upsert-modal">
        <DialogHeader>
          <DialogTitle>
            {initial ? `Edit template: ${initial.template_name}` : 'New WhatsApp template'}
          </DialogTitle>
          <DialogDescription>
            Writes through the canonical <code>POST /api/admin/whatsapp/templates/upsert</code>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Template name</label>
            <Input
              data-testid="wa-upsert-template-name"
              value={form.template_name}
              onChange={(e) => setForm((f) => ({ ...f, template_name: e.target.value }))}
              placeholder="e.g. quote_sent_v1"
              disabled={!!initial}
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Language</label>
            <Input
              data-testid="wa-upsert-language"
              value={form.language}
              onChange={(e) => setForm((f) => ({ ...f, language: e.target.value }))}
              placeholder="en_US"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500">Category</label>
            <Select
              value={form.category}
              onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}
            >
              <SelectTrigger data-testid="wa-upsert-category"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="utility">utility</SelectItem>
                <SelectItem value="marketing">marketing</SelectItem>
                <SelectItem value="authentication">authentication</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Status</label>
            <Select
              value={form.status}
              onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}
            >
              <SelectTrigger data-testid="wa-upsert-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">draft</SelectItem>
                <SelectItem value="approved">approved</SelectItem>
                <SelectItem value="disabled">disabled</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="text-xs text-slate-500">
              Components (JSON array — header / body / footer / buttons)
            </label>
            <Textarea
              data-testid="wa-upsert-components"
              value={form.components_text}
              onChange={(e) => setForm((f) => ({ ...f, components_text: e.target.value }))}
              rows={8}
              className="font-mono text-xs"
            />
          </div>
        </div>

        {error && (
          <div className="mt-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2" data-testid="wa-upsert-error">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wa-upsert-cancel">
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving} data-testid="wa-upsert-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            {initial ? 'Save changes' : 'Create template'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Test-send Modal
// ─────────────────────────────────────────────────────────────────────

const TestSendModal = ({ open, onOpenChange, template }) => {
  const [toPhone, setToPhone] = useState('');
  const [varsText, setVarsText] = useState('[]');
  const [language, setLanguage] = useState(template ? template.language : 'en_US');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setToPhone('');
      setVarsText('[]');
      setLanguage((template && template.language) || 'en_US');
      setResult(null);
      setError(null);
    }
  }, [open, template]);

  const submit = async () => {
    setError(null);
    setResult(null);
    if (!template) return;
    let vars;
    try {
      vars = JSON.parse(varsText || '[]');
      if (!Array.isArray(vars)) throw new Error('variables must be a JSON array of strings');
    } catch (e) {
      setError(`Invalid variables JSON: ${e.message}`);
      return;
    }
    if (!toPhone.trim()) {
      setError('Recipient phone is required (E.164: +15551234567).');
      return;
    }
    setSending(true);
    try {
      const r = await api.post(
        '/whatsapp/send-template',
        {
          to_phone: toPhone.trim(),
          template_name: template.template_name,
          language: language || 'en_US',
          variables: vars.map((v) => String(v)),
        },
        { headers: { 'Idempotency-Key': idemKey('wa-test') } },
      );
      setResult(r.data);
      toast({ title: 'Test send accepted', description: `${template.template_name} → ${toPhone}` });
    } catch (e) {
      const msg = (e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message;
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="wa-test-send-modal">
        <DialogHeader>
          <DialogTitle>Test send</DialogTitle>
          <DialogDescription>
            Sends through the canonical <code>POST /api/whatsapp/send-template</code> (idempotency-protected, mirrored into <code>notifications_log</code>).
          </DialogDescription>
        </DialogHeader>

        {!template ? (
          <div className="text-sm text-slate-500">No template selected.</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2 text-sm">
              <FileText className="h-4 w-4 text-slate-400" />
              <span className="font-medium">{template.template_name}</span>
              {statusBadge(template.status)}
            </div>
            <div>
              <label className="text-xs text-slate-500">Recipient phone (E.164)</label>
              <Input
                data-testid="wa-test-send-phone"
                value={toPhone}
                onChange={(e) => setToPhone(e.target.value)}
                placeholder="+15551234567"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Language</label>
              <Input
                data-testid="wa-test-send-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500">Body variables (JSON array of strings)</label>
              <Textarea
                data-testid="wa-test-send-variables"
                value={varsText}
                onChange={(e) => setVarsText(e.target.value)}
                className="font-mono text-xs"
                rows={3}
              />
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2" data-testid="wa-test-send-error">
            <AlertTriangle className="h-4 w-4" /> {error}
          </div>
        )}
        {result && (
          <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm" data-testid="wa-test-send-result">
            <div className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4" /> Accepted.</div>
            <pre className="text-[11px] mt-2 whitespace-pre-wrap break-all">{JSON.stringify(result, null, 2)}</pre>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wa-test-send-close">
            Close
          </Button>
          <Button onClick={submit} disabled={!template || sending} data-testid="wa-test-send-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
            Send test
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Opt-out add modal
// ─────────────────────────────────────────────────────────────────────

const OptOutModal = ({ open, onOpenChange, onSaved }) => {
  const [phone, setPhone] = useState('');
  const [optOut, setOptOut] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) { setPhone(''); setOptOut(true); setError(null); }
  }, [open]);

  const submit = async () => {
    setError(null);
    if (!phone.trim()) { setError('Phone is required.'); return; }
    setSaving(true);
    try {
      await api.post('/admin/whatsapp/opt-out', {
        phone: phone.trim(),
        opt_out: !!optOut,
      });
      toast({ title: optOut ? 'Opt-out recorded' : 'Opt-in recorded', description: phone });
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (e) {
      const msg = (e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message;
      setError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="wa-optout-modal">
        <DialogHeader>
          <DialogTitle>Set opt-out</DialogTitle>
          <DialogDescription>
            Writes via the canonical <code>POST /api/admin/whatsapp/opt-out</code>.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-slate-500">Phone</label>
            <Input
              data-testid="wa-optout-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+15551234567"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={optOut}
                onChange={(e) => setOptOut(e.target.checked)}
                data-testid="wa-optout-toggle"
              />
              Mark as opted-out (uncheck to re-opt-in)
            </label>
          </div>
          {error && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center gap-2" data-testid="wa-optout-error">
              <AlertTriangle className="h-4 w-4" /> {error}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="wa-optout-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="wa-optout-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────

const WhatsAppAdmin = () => {
  const navigate = useNavigate();

  const [templates, setTemplates] = useState([]);
  const [tplLoading, setTplLoading] = useState(true);
  const [tplError, setTplError] = useState(null);
  const [filter, setFilter] = useState('all');

  const [optOuts, setOptOuts] = useState({ total_opt_outs: 0, last_30_days: 0, recent: [] });
  const [optOutLoading, setOptOutLoading] = useState(true);
  const [optOutError, setOptOutError] = useState(null);

  const [upsertOpen, setUpsertOpen] = useState(false);
  const [upsertInitial, setUpsertInitial] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendTpl, setSendTpl] = useState(null);
  const [optOutOpen, setOptOutOpen] = useState(false);

  const loadTemplates = useCallback(async (statusFilter) => {
    setTplLoading(true);
    setTplError(null);
    try {
      const r = await api.get('/admin/whatsapp/templates', {
        params: statusFilter && statusFilter !== 'all' ? { status: statusFilter } : {},
      });
      setTemplates((r.data && r.data.templates) || []);
    } catch (e) {
      setTplError((e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message);
      setTemplates([]);
    } finally {
      setTplLoading(false);
    }
  }, []);

  const loadOptOuts = useCallback(async () => {
    setOptOutLoading(true);
    setOptOutError(null);
    try {
      const r = await api.get('/admin/whatsapp/opt-outs');
      setOptOuts({
        total_opt_outs: (r.data && r.data.total_opt_outs) || 0,
        last_30_days: (r.data && r.data.last_30_days) || 0,
        recent: (r.data && r.data.recent) || [],
      });
    } catch (e) {
      setOptOutError((e.response && e.response.data && (e.response.data.detail || e.response.data.error)) || e.message);
      setOptOuts({ total_opt_outs: 0, last_30_days: 0, recent: [] });
    } finally {
      setOptOutLoading(false);
    }
  }, []);

  useEffect(() => { loadTemplates(filter); }, [loadTemplates, filter]);
  useEffect(() => { loadOptOuts(); }, [loadOptOuts]);

  const counts = useMemo(() => {
    const out = { all: templates.length, approved: 0, draft: 0, disabled: 0 };
    templates.forEach((t) => {
      const s = (t.status || 'draft').toLowerCase();
      if (out[s] !== undefined) out[s] += 1;
    });
    return out;
  }, [templates]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="whatsapp-admin-page">
      <Toaster />

      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/treatx-admin/notification-ops')}
              data-testid="wa-back-to-ops"
            >
              <ArrowLeft className="h-4 w-4 mr-1" /> Notification Ops
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-emerald-600" />
                WhatsApp Admin
              </h1>
              <p className="text-xs text-slate-500">
                Reads & writes through the canonical WhatsApp admin endpoints. Backend services
                (<code>whatsapp_template_service</code>, <code>whatsapp_outbound_service</code>,
                <code> whatsapp_observability_bridge</code>) are the source of truth.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => { loadTemplates(filter); loadOptOuts(); }}
              data-testid="wa-refresh"
            >
              <RefreshCw className="h-4 w-4 mr-1" /> Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stat row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
          <Card data-testid="wa-stat-templates"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Templates</div>
            <div className="text-2xl font-semibold">{counts.all}</div>
            <div className="text-[11px] text-slate-500 mt-1">
              {counts.approved} approved · {counts.draft} draft · {counts.disabled} disabled
            </div>
          </CardContent></Card>
          <Card data-testid="wa-stat-optouts-total"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Total opt-outs</div>
            <div className="text-2xl font-semibold">{optOuts.total_opt_outs}</div>
            <div className="text-[11px] text-slate-500 mt-1">All-time</div>
          </CardContent></Card>
          <Card data-testid="wa-stat-optouts-30d"><CardContent className="p-4">
            <div className="text-xs uppercase text-slate-500">Opt-outs (30d)</div>
            <div className="text-2xl font-semibold">{optOuts.last_30_days}</div>
            <div className="text-[11px] text-slate-500 mt-1">Rolling window</div>
          </CardContent></Card>
          <Card data-testid="wa-stat-canonical"><CardContent className="p-4 flex items-center gap-3">
            <Shield className="h-7 w-7 text-emerald-600" />
            <div>
              <div className="text-xs uppercase text-slate-500">Source of truth</div>
              <div className="text-sm font-medium">Canonical backend</div>
              <div className="text-[11px] text-slate-500">No projection / no cache</div>
            </div>
          </CardContent></Card>
        </div>

        <Tabs defaultValue="templates" className="w-full">
          <TabsList data-testid="wa-tabs">
            <TabsTrigger value="templates" data-testid="wa-tab-templates">
              <FileText className="h-4 w-4 mr-1" /> Templates
            </TabsTrigger>
            <TabsTrigger value="opt-outs" data-testid="wa-tab-optouts">
              <ShieldOff className="h-4 w-4 mr-1" /> Opt-outs
            </TabsTrigger>
          </TabsList>

          {/* ── Templates tab ── */}
          <TabsContent value="templates" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div>
                  <CardTitle className="text-base">WhatsApp templates</CardTitle>
                  <CardDescription>
                    Reads <code>GET /api/admin/whatsapp/templates</code>; edits via canonical upsert.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Select value={filter} onValueChange={setFilter}>
                    <SelectTrigger className="w-[140px]" data-testid="wa-template-filter">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All ({counts.all})</SelectItem>
                      <SelectItem value="approved">Approved ({counts.approved})</SelectItem>
                      <SelectItem value="draft">Draft ({counts.draft})</SelectItem>
                      <SelectItem value="disabled">Disabled ({counts.disabled})</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    onClick={() => { setUpsertInitial(null); setUpsertOpen(true); }}
                    data-testid="wa-new-template-btn"
                  >
                    <Plus className="h-4 w-4 mr-1" /> New template
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {tplLoading ? (
                  <div className="py-8 flex items-center justify-center text-slate-500" data-testid="wa-templates-loading">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading templates…
                  </div>
                ) : tplError ? (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="wa-templates-error">
                    {tplError}
                  </div>
                ) : templates.length === 0 ? (
                  <div className="py-10 text-center text-slate-500" data-testid="wa-templates-empty">
                    <MessageSquare className="h-7 w-7 mx-auto mb-2 text-slate-300" />
                    <div className="text-sm">No templates yet.</div>
                    <div className="text-[11px] mt-1">Click <strong>New template</strong> to upsert one through the canonical endpoint.</div>
                  </div>
                ) : (
                  <div className="border rounded-xl overflow-hidden" data-testid="wa-templates-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase text-slate-500">
                          <th className="px-3 py-2">Name</th>
                          <th className="px-3 py-2">Status</th>
                          <th className="px-3 py-2">Language</th>
                          <th className="px-3 py-2">Category</th>
                          <th className="px-3 py-2">Updated</th>
                          <th className="px-3 py-2 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {templates.map((t) => (
                          <tr
                            key={`${t.template_name}-${t.language}`}
                            className="border-t hover:bg-slate-50"
                            data-testid={`wa-template-row-${t.template_name}`}
                          >
                            <td className="px-3 py-2 font-mono text-xs">{t.template_name}</td>
                            <td className="px-3 py-2">{statusBadge(t.status)}</td>
                            <td className="px-3 py-2">
                              <Badge variant="outline" className="text-[10px]">{t.language || 'en_US'}</Badge>
                            </td>
                            <td className="px-3 py-2">{categoryBadge(t.category)}</td>
                            <td className="px-3 py-2 text-xs text-slate-500">{fmt(t.updated_at_utc || t.updated_at)}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => { setSendTpl(t); setSendOpen(true); }}
                                data-testid={`wa-template-test-${t.template_name}`}
                              >
                                <Send className="h-3.5 w-3.5 mr-1" /> Test send
                              </Button>
                              <Button
                                size="sm" variant="ghost"
                                onClick={() => { setUpsertInitial(t); setUpsertOpen(true); }}
                                data-testid={`wa-template-edit-${t.template_name}`}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* ── Opt-outs tab ── */}
          <TabsContent value="opt-outs" className="mt-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div>
                  <CardTitle className="text-base">Opt-outs</CardTitle>
                  <CardDescription>
                    Reads <code>GET /api/admin/whatsapp/opt-outs</code> (phones masked). Writes via canonical opt-out endpoint.
                  </CardDescription>
                </div>
                <Button onClick={() => setOptOutOpen(true)} data-testid="wa-add-optout-btn">
                  <Plus className="h-4 w-4 mr-1" /> Set opt-out
                </Button>
              </CardHeader>
              <CardContent className="pt-0">
                {optOutLoading ? (
                  <div className="py-8 flex items-center justify-center text-slate-500" data-testid="wa-optouts-loading">
                    <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading opt-outs…
                  </div>
                ) : optOutError ? (
                  <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-sm" data-testid="wa-optouts-error">
                    {optOutError}
                  </div>
                ) : optOuts.recent.length === 0 ? (
                  <div className="py-10 text-center text-slate-500" data-testid="wa-optouts-empty">
                    <ShieldOff className="h-7 w-7 mx-auto mb-2 text-slate-300" />
                    <div className="text-sm">No recent opt-outs.</div>
                    <div className="text-[11px] mt-1">The canonical opt-out collection is empty.</div>
                  </div>
                ) : (
                  <div className="border rounded-xl overflow-hidden" data-testid="wa-optouts-table">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-50">
                        <tr className="text-left text-xs uppercase text-slate-500">
                          <th className="px-3 py-2">Phone</th>
                          <th className="px-3 py-2">Source</th>
                          <th className="px-3 py-2">Keyword</th>
                          <th className="px-3 py-2">Opted-out at</th>
                          <th className="px-3 py-2">Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {optOuts.recent.map((row, i) => (
                          <tr key={i} className="border-t" data-testid={`wa-optout-row-${i}`}>
                            <td className="px-3 py-2 font-mono text-xs flex items-center gap-1">
                              <Phone className="h-3 w-3 text-slate-400" /> {row.phone_masked || '***'}
                            </td>
                            <td className="px-3 py-2 text-xs">{row.source || '—'}</td>
                            <td className="px-3 py-2 text-xs">{row.keyword || '—'}</td>
                            <td className="px-3 py-2 text-xs">{fmt(row.opted_out_at)}</td>
                            <td className="px-3 py-2 text-xs">{fmt(row.updated_at_utc)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <div className="mt-6 text-[11px] text-slate-500 flex items-center gap-1" data-testid="wa-canonical-source-note">
          <Info className="h-3 w-3" />
          All data and writes flow through canonical endpoints under
          <code className="mx-1">/api/admin/whatsapp/*</code> and
          <code className="mx-1">/api/whatsapp/send-template</code>.
          No projection, no parallel template store.
        </div>
      </div>

      <UpsertModal
        open={upsertOpen}
        onOpenChange={setUpsertOpen}
        initial={upsertInitial}
        onSaved={() => loadTemplates(filter)}
      />
      <TestSendModal
        open={sendOpen}
        onOpenChange={setSendOpen}
        template={sendTpl}
      />
      <OptOutModal
        open={optOutOpen}
        onOpenChange={setOptOutOpen}
        onSaved={loadOptOuts}
      />
    </div>
  );
};

export default WhatsAppAdmin;
